import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { CreditTransaction } from '../list';
import { useMutation, useQueryClient } from 'react-query';
import { updateTransaction } from 'services/credits_v2';
import { useSnackbar } from 'notistack';

interface EditTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: CreditTransaction | null;
  onSuccess?: () => void;
}

export const EditTransactionDialog: React.FC<EditTransactionDialogProps> = ({
  open,
  onClose,
  transaction,
  onSuccess
}) => {
  const [amount, setAmount] = useState<number>(0);
  const [type, setType] = useState<'credit' | 'debit'>('credit');
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (transaction) {
      setAmount(transaction.amount);
      setType(transaction.type);
    }
  }, [transaction]);

  const updateMutation = useMutation(
    (data: { id: number; amount: number; type: 'credit' | 'debit' }) => 
      updateTransaction(data.id, data.amount, data.type),
    {
      onSuccess: () => {
        enqueueSnackbar('Transaction updated successfully', { variant: 'success' });
        queryClient.invalidateQueries(['credit-v2s']);
        onClose();
        onSuccess?.();
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.error?.message || 'Failed to update transaction', 
          { variant: 'error' }
        );
      }
    }
  );

  const handleSubmit = () => {
    if (!transaction) return;
    
    if (amount <= 0) {
      enqueueSnackbar('Amount must be greater than 0', { variant: 'error' });
      return;
    }

    updateMutation.mutate({
      id: transaction.id,
      amount,
      type
    });
  };

  if (!transaction) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '500px',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #eee',
        pb: 2
      }}>
        <Typography variant="h6">Edit Transaction</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Client
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {transaction?.client?.name || 'Unknown Client'}
            {transaction?.client?.pseudo ? ` (${transaction.client.pseudo})` : ''}
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
            <Select
              labelId="transaction-type-label"
              value={type}
              onChange={(e) => setType(e.target.value as 'credit' | 'debit')}
              label="Transaction Type"
              disabled={transaction.is_paid}
            >
              <MenuItem value="credit">Credit</MenuItem>
              <MenuItem value="debit">Debit</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="Amount"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value))}
            variant="outlined"
            disabled={transaction.is_paid}
            InputProps={{
              endAdornment: <Typography variant="body2">TND</Typography>
            }}
          />
        </Box>
        
        {transaction.is_paid && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This transaction has been paid and cannot be edited.
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={transaction.is_paid || updateMutation.isLoading}
        >
          {updateMutation.isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
